import { useQuery } from '@tanstack/react-query';
import { Button, ButtonVariants } from '@components/button/Button';
import { errorToast } from '@components/toasts/Toasts';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import cx from 'classnames';
import { useState } from 'react';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useVipTiers } from 'restaurantAdmin/hooks/useVipTiers';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import {
  getCoupons,
  reorderVipTiers,
  type Coupon,
  type VipTier,
} from './apiHelpers';
import { CouponDrawer } from './CouponDrawer';
import { CouponsTable } from './CouponsTable';
import { DeleteCouponConfirmationModal } from './DeleteCouponConfirmationModal';
import { DeleteVipTierConfirmationModal } from './DeleteVipTierConfirmationModal';
import { VipTierDrawer } from './VipTierDrawer';
import { VipTiersTable } from './VipTiersTable';
import styles from './VipProgramSection.scss';
import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';

export interface VipProgramSectionProps {
  sectionTitleId: string;
  variant?: 'page' | 'section';
}

type VipProgramTab = 'tiers' | 'coupons';

export const VipProgramSection = ({
  sectionTitleId,
  variant = 'section',
}: VipProgramSectionProps) => {
  const { id: restaurantId } = useRestaurant();
  const { isEnabled } = useFeatureFlagContext();
  const {
    isOpen: isCouponDrawerOpen,
    open: openCouponDrawer,
    close: closeCouponDrawer,
  } = useIsOpen();
  const {
    isOpen: isVipTierDrawerOpen,
    open: openVipTierDrawer,
    close: closeVipTierDrawer,
  } = useIsOpen();
  const {
    isOpen: isDeleteModalOpen,
    open: openDeleteModal,
    close: closeDeleteModal,
  } = useIsOpen();
  const {
    isOpen: isDeleteVipTierModalOpen,
    open: openDeleteVipTierModal,
    close: closeDeleteVipTierModal,
  } = useIsOpen();
  const [activeTab, setActiveTab] = useState<VipProgramTab>(
    isEnabled('vipProgramTiers') ? 'tiers' : 'coupons',
  );
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon>();
  const [couponToDelete, setCouponToDelete] = useState<Coupon>();
  const [selectedVipTier, setSelectedVipTier] = useState<VipTier>();
  const [vipTierToDelete, setVipTierToDelete] = useState<VipTier>();

  const { data: coupons = [], refetch: refetchCoupons } = useQuery({
    queryKey: ['restaurants', restaurantId, 'coupons'],
    queryFn: async () => getCoupons(restaurantId),
  });
  const { data: vipTiers, refetch: refetchVipTiers } = useVipTiers();

  const handleAddVipTier = () => {
    setSelectedVipTier(undefined);
    openVipTierDrawer();
  };

  const handleAddCoupon = () => {
    setSelectedCoupon(undefined);
    openCouponDrawer();
  };

  const handleEditCoupon = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    openCouponDrawer();
  };

  const handleDeleteCoupon = (coupon: Coupon) => {
    setCouponToDelete(coupon);
    openDeleteModal();
  };

  const handleEditVipTier = (vipTier: VipTier) => {
    setSelectedVipTier(vipTier);
    openVipTierDrawer();
  };

  const handleDeleteVipTier = (vipTier: VipTier) => {
    setVipTierToDelete(vipTier);
    openDeleteVipTierModal();
  };

  const handleReorderVipTiers = async (vipTierIds: string[]) => {
    try {
      await reorderVipTiers(restaurantId, vipTierIds);
      void refetchVipTiers();
    } catch (error) {
      errorToast({ message: 'Unable to reorder VIP tiers. Please try again.' });
      reportAppError(error);
    }
  };

  const isTiersTabActive = activeTab === 'tiers';

  const content = (
    <>
      <SettingsOutletHeader
        actions={
          <Button
            className={styles.addCouponButton}
            iconName="plus"
            label={isTiersTabActive ? 'Add Tier' : 'Add Code'}
            onClick={isTiersTabActive ? handleAddVipTier : handleAddCoupon}
            variant={ButtonVariants.Primary}
          />
        }
        title="VIP Program"
        titleId={sectionTitleId}
        variant={variant}
      />
      <div
        className={styles.tabBar}
        role="tablist"
        aria-label="VIP program sections"
      >
        {isEnabled('vipProgramTiers') && (
          <button
            type="button"
            role="tab"
            aria-selected={isTiersTabActive}
            className={cx(styles.tab, {
              [styles.tabSelected]: isTiersTabActive,
            })}
            onClick={() => {
              setActiveTab('tiers');
            }}
          >
            Tiers
          </button>
        )}
        {isEnabled('vipProgramCodes') && (
          <button
            type="button"
            role="tab"
            aria-selected={!isTiersTabActive}
            className={cx(styles.tab, {
              [styles.tabSelected]: !isTiersTabActive,
            })}
            onClick={() => {
              setActiveTab('coupons');
            }}
          >
            Codes
          </button>
        )}
      </div>
      {isTiersTabActive ? (
        <VipTiersTable
          onDeleteVipTier={handleDeleteVipTier}
          onEditVipTier={handleEditVipTier}
          onReorderVipTiers={(vipTierIds) => {
            void handleReorderVipTiers(vipTierIds);
          }}
          vipTiers={vipTiers}
        />
      ) : (
        <CouponsTable
          coupons={coupons}
          onDeleteCoupon={handleDeleteCoupon}
          onEditCoupon={handleEditCoupon}
        />
      )}

      <VipTierDrawer
        isOpen={isVipTierDrawerOpen}
        onClose={closeVipTierDrawer}
        onSaved={() => {
          void refetchVipTiers();
        }}
        vipTier={selectedVipTier}
      />

      <CouponDrawer
        coupon={selectedCoupon}
        isOpen={isCouponDrawerOpen}
        onClose={closeCouponDrawer}
        onSaved={() => {
          void refetchCoupons();
        }}
      />

      {vipTierToDelete && (
        <DeleteVipTierConfirmationModal
          closeModal={closeDeleteVipTierModal}
          isOpen={isDeleteVipTierModalOpen}
          onDeleted={() => {
            void refetchVipTiers();
          }}
          restaurantId={restaurantId}
          setVipTierToDelete={setVipTierToDelete}
          vipTier={vipTierToDelete}
        />
      )}

      {couponToDelete && (
        <DeleteCouponConfirmationModal
          closeModal={closeDeleteModal}
          coupon={couponToDelete}
          isOpen={isDeleteModalOpen}
          onDeleted={() => {
            void refetchCoupons();
          }}
          restaurantId={restaurantId}
          setCouponToDelete={setCouponToDelete}
        />
      )}
    </>
  );

  if (variant === 'page') {
    return content;
  }

  return (
    <section className={styles.section} aria-labelledby={sectionTitleId}>
      {content}
    </section>
  );
};
